import React from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as HasMembershipIcon } from '@pkg/assets/icons/has-membership.svg';
import { ChipColor, ChipSize } from '@pkg/components/Sections/Profile/Chip';
import { ChipMember } from '@pkg/components/Sections/Profile/Chips/ChipMember';
import { DepButton, DepButtonVariant } from '@pkg/components/deprecated';
import { useAppDispatch, useDeleteMember, useDeleteMemberInvitation, useGroupId } from '@pkg/hooks';

import { type ResourceId } from '@app/client';
import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { CancelIcon, TrashIcon } from '@app/pages/Prototype/icons';
import { addNotification } from '@app/store/actions/notification';

import './Member.scss';

export interface MemberProps {
    memberId: ResourceId;
    permission: 'Admin' | 'Member' | 'Invitee';
    isOwner?: boolean;
    twitter?: string;
    primaryYat?: EmojiId;
    inviteId?: ResourceId;
}

export function Member({ memberId, isOwner, twitter, permission, primaryYat, inviteId }: MemberProps) {
    const { t: translate } = useTranslation('groups');
    const dispatch = useAppDispatch();

    const groupId = useGroupId();
    const { mutate: deleteMember } = useDeleteMember();
    const { mutate: deleteMemberInvitation } = useDeleteMemberInvitation();

    function handleRemoveClick() {
        if (!groupId || !memberId) return;

        deleteMember(
            { groupId, memberId },
            {
                onSuccess() {
                    dispatch(addNotification({ message: 'Removed member' }));
                },
            },
        );
    }

    function handleUninviteClick() {
        if (!groupId || !inviteId) return;

        deleteMemberInvitation(
            { groupId, inviteId },
            {
                onSuccess() {
                    dispatch(addNotification({ message: 'Removed invite' }));
                },
            },
        );
    }

    return (
        <div className="yat-group-member">
            <ChipMember
                to={`/${primaryYat}`}
                twitter={twitter}
                yat={primaryYat}
                color={ChipColor.Transparent}
                size={ChipSize.Small}
            />

            {permission === 'Admin' && (
                <DepButton size="x-small" variant={DepButtonVariant.legendary} interactive={false}>
                    <span className="yat-group-member__owner-icon">
                        <HasMembershipIcon />
                    </span>

                    {translate('groups_owner')}
                </DepButton>
            )}

            {permission === 'Member' && isOwner && (
                <DepButton size="x-small" variant={DepButtonVariant.muted} onClick={handleRemoveClick}>
                    <span className="yat-group-member__remove-icon">
                        <TrashIcon />
                    </span>

                    {translate('groups_remove')}
                </DepButton>
            )}

            {permission === 'Invitee' && (
                <DepButton
                    size="x-small"
                    variant={DepButtonVariant.muted}
                    onClick={handleUninviteClick}
                    disabled={!isOwner}
                >
                    {isOwner && (
                        <span className="yat-group-member__uninvite-icon">
                            <CancelIcon />
                        </span>
                    )}

                    {translate(isOwner ? 'groups_uninvite' : 'groups_invited')}
                </DepButton>
            )}
        </div>
    );
}
